import { useRef, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { PenLine, Upload, Eraser, Save, Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StaffSignature({ token }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [current, setCurrent] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/staff/signature`, { headers: { Authorization: `Bearer ${token}` } });
      if (data.has_signature) setCurrent(data.signature);
    } catch { /* ignore */ }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const ctx = () => canvasRef.current.getContext("2d");
  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (canvasRef.current.width / r.width), y: (t.clientY - r.top) * (canvasRef.current.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const c = ctx(); const p = pos(e); c.beginPath(); c.moveTo(p.x, p.y); };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const c = ctx(); const p = pos(e);
    c.lineWidth = 2.5; c.lineCap = "round"; c.strokeStyle = "#0b0b0c";
    c.lineTo(p.x, p.y); c.stroke(); setDirty(true);
  };
  const end = () => { drawing.current = false; };

  const clearPad = () => { const c = canvasRef.current; ctx().clearRect(0, 0, c.width, c.height); setDirty(false); };

  const onUpload = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!/\.(png)$/i.test(f.name)) { setErr("Nahrajte podpis vo formáte PNG (s priehľadným pozadím)."); return; }
    const reader = new FileReader();
    reader.onload = () => { setCurrent(reader.result); setDirty(false); setErr(""); };
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setErr("");
    let dataUrl = current;
    if (dirty) dataUrl = canvasRef.current.toDataURL("image/png");
    if (!dataUrl || !dataUrl.startsWith("data:image/png")) { setErr("Najprv nakreslite alebo nahrajte podpis."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/staff/signature`, { signature: dataUrl }, { headers: { Authorization: `Bearer ${token}` } });
      setCurrent(dataUrl); setDirty(false); setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (e) { setErr(e.response?.data?.detail || "Uloženie zlyhalo."); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="staff-signature">
      <div className="mb-6">
        <h2 className="font-display text-2xl mb-1 flex items-center gap-2"><PenLine size={22} className="text-[#D4AF37]" /> Môj podpis</h2>
        <p className="text-white/40 text-sm">Nakreslite alebo nahrajte svoj podpis (PNG). Uloží sa len vám a použijete ho pri elektronickom podpisovaní PDF.</p>
      </div>

      {current && (
        <div className="mb-5">
          <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Aktuálny uložený podpis</p>
          <div className="inline-block bg-white p-3 border border-white/10" data-testid="staff-signature-current">
            <img src={current} alt="Podpis" className="h-16 w-auto" />
          </div>
        </div>
      )}

      <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Nakreslite podpis</p>
      <canvas
        ref={canvasRef}
        width={600}
        height={180}
        data-testid="staff-signature-pad"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="bg-white w-full max-w-xl rounded-sm touch-none cursor-crosshair border border-white/10"
        style={{ aspectRatio: "600 / 180" }}
      />
      {err && <p className="text-red-400 text-sm mt-3" data-testid="staff-signature-error">{err}</p>}
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <button onClick={clearPad} data-testid="staff-signature-clear" className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-sm text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"><Eraser size={15} /> Vymazať</button>
        <label data-testid="staff-signature-upload" className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-sm text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] cursor-pointer transition-colors">
          <Upload size={15} /> Nahrať PNG<input type="file" accept="image/png" onChange={onUpload} className="hidden" />
        </label>
        <button onClick={save} disabled={saving} data-testid="staff-signature-save" className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-5 py-2.5 text-sm hover:bg-[#F2E3C6] transition-colors disabled:opacity-50">
          {saving ? <Loader2 size={15} className="animate-spin" /> : saved ? <Check size={15} /> : <Save size={15} />} {saved ? "Uložené" : "Uložiť podpis"}
        </button>
      </div>
    </div>
  );
}
